import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Award, GraduationCap, Clock, ArrowRight } from 'lucide-react';

export default function Hero() {
  const navigate = useNavigate();

  return (
    <section id="inicio" className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
      {/* Background Image - More visible */}
      <div className="absolute inset-0 z-0">
        {/* Base image layer - higher opacity for visibility */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url("/hero-placeholder.jpg")' }}
        />

        {/* Gradient overlay - less opaque to show image */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-blue-900/70 to-slate-900/80" />

        {/* Subtle color accent */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-60" />

        {/* Bottom fade to next section */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />
      </div>

      {/* Decorative elements */}
      <div className="absolute inset-0 z-[1] overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-500/20 rounded-full blur-3xl" />
      </div>

      <div className="container relative z-10 px-4 pt-20 text-center text-white">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          {/* Badge */}
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full text-blue-200 text-sm font-medium mb-6 backdrop-blur-sm"
          >
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            Inscripciones Abiertas 2026
          </motion.span>

          <h1 className="text-4xl sm:text-6xl md:text-7xl font-extrabold font-heading mb-6 tracking-tight">
            ¡Forma tu Futuro con <br className="hidden sm:block" />
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
              INSTITUTO VALPARAÍSO!
            </span>
          </h1>

          <p className="text-lg sm:text-xl md:text-2xl font-light mb-10 max-w-3xl mx-auto text-slate-300 leading-relaxed">
            Capacitación profesional de excelencia en Valparaíso. <br className="hidden md:block" />
            Certificaciones reconocidas, modalidad presencial y online.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/cursos')}
              className="group bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-8 py-4 rounded-xl text-lg font-bold transition-all shadow-xl shadow-blue-500/30 flex items-center justify-center gap-2"
            >
              Inscríbete Ahora
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/cursos')}
              className="px-8 py-4 rounded-xl text-lg font-semibold bg-white/10 border border-white/20 hover:bg-white/20 text-white transition-all backdrop-blur-sm"
            >
              Ver Cursos
            </motion.button>
          </div>

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10 pt-8 border-t border-white/10 max-w-4xl mx-auto"
          >
            <div className="flex items-center gap-3 px-4 py-2 bg-white/5 rounded-full backdrop-blur-sm">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <Award className="w-5 h-5 text-white" />
              </div>
              <span className="font-medium text-white">OTEC Certificada NCH 2728</span>
            </div>
            <div className="flex items-center gap-3 px-4 py-2 bg-white/5 rounded-full backdrop-blur-sm">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <span className="font-medium text-white">Autorizados por la Subsecretaría de Prevención del Delito</span>
            </div>
            <div className="flex items-center gap-3 px-4 py-2 bg-white/5 rounded-full backdrop-blur-sm">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <span className="font-medium text-white">Aula Virtual 24/7</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
